import { useState, useEffect } from 'react';
import { propertyApi, ApiRequestError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import RichTextEditor from '@/components/RichTextEditor';
import RichTextViewer from '@/components/RichTextViewer';

export default function PropertyPage() {
  const { user } = useAuth();
  const [property, setProperty] = useState<{
    property_name: string;
    property_info: string | null;
    default_checkin_time: string;
    default_checkout_time: string;
    updated_at: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const canEdit = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const { property } = await propertyApi.get();
        setProperty(property);
        setEditContent(property.property_info || '');
      } catch (err) {
        if (err instanceof ApiRequestError) {
          setError(err.message);
        } else {
          setError('Failed to load property information');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
  }, []);

  const handleEdit = () => {
    setEditContent(property?.property_info || '');
    setIsEditing(true);
    setSaveError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditContent(property?.property_info || '');
    setSaveError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      await propertyApi.update({ property_info: editContent });
      setProperty((prev) =>
        prev
          ? { ...prev, property_info: editContent, updated_at: new Date().toISOString() }
          : null
      );
      setIsEditing(false);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setSaveError(err.message);
      } else {
        setSaveError('Failed to save changes');
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Property Information</h1>
        {canEdit && !isEditing && (
          <button onClick={handleEdit} className="btn-primary">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            Edit
          </button>
        )}
      </div>

      <div className="card p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {property?.property_name || 'Apartment 5H Calendar'}
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Arrival Time</h3>
            <p className="mt-1 text-gray-900">
              {property?.default_checkin_time || '12:00'} (Noon)
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Departure Time</h3>
            <p className="mt-1 text-gray-900">
              {property?.default_checkout_time || '12:00'} (Noon)
            </p>
          </div>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-sm font-medium text-gray-900 mb-4">
            Property Details & Access Information
          </h3>

          {isEditing ? (
            <div className="space-y-4">
              <RichTextEditor
                content={editContent}
                onChange={setEditContent}
                placeholder="Enter property details, access instructions, WiFi info, house rules, etc..."
              />

              {saveError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {saveError}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancel}
                  className="btn-secondary"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn-primary"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          ) : property?.property_info ? (
            <RichTextViewer content={property.property_info} />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              <p className="mt-4">No property information has been added yet.</p>
              {canEdit && (
                <button onClick={handleEdit} className="mt-4 link">
                  Add property information
                </button>
              )}
            </div>
          )}
        </div>

        {property?.updated_at && (
          <div className="text-sm text-gray-500 pt-4 border-t">
            Last updated:{' '}
            {new Date(property.updated_at).toLocaleDateString('en-US', {
              dateStyle: 'long',
            })}
          </div>
        )}
      </div>
    </div>
  );
}
